import {
  useDialogListener as useDialogListenerBase,
  useDialogTrigger as useDialogTriggerBase,
  emitCustomEvent as emitCustomEventBase
} from '@satellite/composables/useEventBasedDialog';

import type { ISpot, AssetContainerType, YardTaskFlags } from '@nova/core';

export type OpenSelectSpotDialogEvent = {
  onSpotSelected: (spot: ISpot) => void;
  includeClosedSpots?: boolean | 'disabled';
  includeAssignedSpots?: boolean | 'disabled';
  includeReservedSpots?: boolean | 'disabled';
  includeAssociatedToActiveMoveTasks?: boolean | 'disabled';
  assetType?: AssetContainerType;
};

export type OpenSelectAssetDialogEvent = {
  onAssetSelected: (assetId: string, spotId: string) => void;
  includeWaitingAssignmentAssets?: boolean | 'disabled';
  includeAssignedAssets?: boolean | 'disabled';
  includeAssociatedToActiveMoveTasks?: boolean | 'disabled';
};

export type OpenEditTaskDialogEvent = {
  taskId: string;
  onTaskUpdated?: (task: unknown) => void;
};

export type OpenReportIssueDialogEvent = {
  taskId: string;
  type: 'move' | 'check-asset' | 'check-spot' | 'check-area';
  tags?: YardTaskFlags[];
  onIssueReported?: (comment: string) => void;
};

export type OpenClearIssueDialogEvent = {
  taskId: string;
  type: 'move' | 'check-asset' | 'check-spot' | 'check-area';
  tags?: YardTaskFlags[];
  onIssueCleared?: () => void;
};

export type CirceCustomEventMap = {
  'open-select-spot-dialog': CustomEvent<OpenSelectSpotDialogEvent>;
  'open-select-asset-dialog': CustomEvent<OpenSelectAssetDialogEvent>;
  'open-edit-task-dialog': CustomEvent<OpenEditTaskDialogEvent>;
  'open-report-issue-dialog': CustomEvent<OpenReportIssueDialogEvent>;
  'open-clear-issue-dialog': CustomEvent<OpenClearIssueDialogEvent>;
};

/**
 * Composable for listening to dialog events (used in dialog components)
 */
export function useDialogListener<K extends keyof CirceCustomEventMap>(dialogName: K) {
  return useDialogListenerBase<CirceCustomEventMap, K>(dialogName);
}

/**
 * Composable for triggering dialog events (used in components that open dialogs)
 */
export function useDialogTrigger<K extends keyof CirceCustomEventMap>(dialogName: K) {
  return useDialogTriggerBase<CirceCustomEventMap, K>(dialogName);
}

/**
 * Emit a custom event (overload without data)
 */
export function emitCustomEvent<K extends keyof CirceCustomEventMap>(
  eventName: K,
  target: EventTarget
): void;

/**
 * Emit a custom event (overload with data)
 */
export function emitCustomEvent<K extends keyof CirceCustomEventMap>(
  eventName: K,
  data: CirceCustomEventMap[K]['detail'],
  target?: EventTarget
): void;

/**
 * Emit a custom event
 */
export function emitCustomEvent<K extends keyof CirceCustomEventMap>(
  eventName: K,
  dataOrTarget?: CirceCustomEventMap[K]['detail'] | EventTarget,
  maybeTarget?: EventTarget
): void {
  emitCustomEventBase<CirceCustomEventMap, K>(eventName, dataOrTarget, maybeTarget);
}
